import React from "react";
import { MdAdd, MdRemove } from "react-icons/md";
import { getTrackColors, TRACK_KEYS } from "../utils/trackHelpers";

interface EditModeControlsProps {
  trackKey: string;
  sessions: any[];
  editSessionTime: string;
  editPlayerCount: number;
  isSaving?: boolean;
  seatsTitle?: string;
  onSessionChange: (sessionId: string) => void;
  onIncrement: () => void;
  onDecrement: () => void;
  onSave: () => void;
  onCancel: () => void;
}

export const EditModeControls: React.FC<EditModeControlsProps> = ({
  trackKey,
  sessions,
  editSessionTime,
  editPlayerCount,
  isSaving = false,
  seatsTitle,
  onSessionChange,
  onIncrement,
  onDecrement,
  onSave,
  onCancel,
}) => {
  const colors = getTrackColors(trackKey);
  const isArcadeCard = trackKey === TRACK_KEYS.ARCADE;

  return (
    <div className="tw-space-y-3">
      {/* Session Time / Game Option Dropdown */}
      <div className="tw-bg-[#555758] tw-rounded-lg tw-py-3 tw-px-4">
        <div className="tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-white tw-text-[14px]">{isArcadeCard ? "Game Option" : "Session Time"}</span>
          <select
            value={editSessionTime}
            onChange={(e) => onSessionChange(e.target.value)}
            className="tw-bg-gray-700 tw-text-white tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-px-2.5 tw-py-2 tw-rounded-lg tw-border-none tw-outline-none tw-appearance-none tw-cursor-pointer tw-min-w-[100px]"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: "right 8px center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "16px",
            }}
          >
            {sessions.map((session, index) => {
              const { selectedOptions, title } = session.variantsSelectedObject;
              const timeOption = selectedOptions?.find(
                (option: any) => option.name?.toLowerCase().includes("time") || option.name?.toLowerCase().includes("slot")
              );
              const gamesOption = selectedOptions?.find(
                (option: any) => option.name?.toLowerCase().includes("games")
              );
              
              let displayText = "";
              if (isArcadeCard && gamesOption) {
                displayText = `${gamesOption.value} games`;
              } else if (timeOption?.value) {
                displayText = timeOption.value;
              } else if (title) {
                // Fallback: Extract time from variant title
                const titleParts = title.split("|");
                if (titleParts.length > 1) {
                  displayText = titleParts[1]?.trim() || `Session ${index + 1}`;
                } else {
                  // Try to match time pattern
                  const timeMatch = title.match(/\d{1,2}:\d{2}\s*(AM|PM|am|pm)?/i);
                  displayText = timeMatch ? timeMatch[0] : `Session ${index + 1}`;
                }
              } else {
                displayText = `Session ${index + 1}`;
              }

              return (
                <option key={session.variantsSelectedObject.id} value={session.variantsSelectedObject.id}>
                  {displayText}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Players/Quantity Count with +/- buttons */}
      <div className="tw-bg-[#555758] tw-rounded-lg tw-py-3 tw-px-4">
        <div className="tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-white tw-text-[14px]">{seatsTitle || "Drivers"}</span>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              onClick={onDecrement}
              disabled={isSaving || editPlayerCount <= 1}
              className={`tw-bg-gray-600 hover:tw-bg-gray-500 tw-text-white tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-[12px] ${
                isSaving || editPlayerCount <= 1 ? "tw-opacity-50 tw-cursor-not-allowed" : ""
              }`}
            >
              <MdRemove />
            </button>
            <span className="tw-text-white tw-text-[14px] tw-font-semibold tw-leading-[21px] tw-tracking-[0.6px] tw-min-w-[20px] tw-text-center">
              {editPlayerCount}
            </span>
            <button
              onClick={onIncrement}
              disabled={isSaving}
              className={`tw-bg-gray-600 hover:tw-bg-gray-500 tw-text-white tw-w-6 tw-h-6 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-[12px] ${
                isSaving ? "tw-opacity-50 tw-cursor-not-allowed" : ""
              }`}
            >
              <MdAdd />
            </button>
          </div>
        </div>
      </div>

      {/* Cancel and Save Changes buttons */}
      <div className="tw-flex tw-gap-2 tw-mt-4">
        <button
          onClick={onCancel}
          disabled={isSaving}
          className={`tw-flex-1 tw-bg-gray-600 hover:tw-bg-gray-500 tw-text-white tw-text-[14px] tw-font-medium tw-py-2 tw-rounded-full tw-transition-colors ${
            isSaving ? "tw-opacity-50 tw-cursor-not-allowed" : ""
          }`}
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={isSaving}
          className={`tw-flex-1 tw-text-white tw-text-[14px] tw-font-medium tw-py-2 tw-rounded-full tw-transition-colors ${colors.buttonGradient} ${
            isSaving ? "tw-opacity-75" : ""
          }`}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

